import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface ActiveTimerWithResource {
  id: string;
  resource_id: string;
  started_at: string;
  resource: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

/**
 * Fetches all resources currently clocked in to a booking (active job_time_entries).
 * Used for "Team working on job" / "Resources working on job" lists.
 */
export function useBookingActiveTimers(bookingId: string | undefined) {
  const [activeTimers, setActiveTimers] = useState<ActiveTimerWithResource[]>([]);
  const [tick, setTick] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchActive = useCallback(async () => {
    if (!bookingId) return;

    const { data, error } = await supabase
      .from('job_time_entries')
      .select(`
        id,
        resource_id,
        started_at,
        resources:resource_id (
          id,
          first_name,
          last_name,
          avatar_url
        )
      `)
      .eq('booking_id', bookingId)
      .is('stopped_at', null)
      .order('started_at', { ascending: true });

    if (error) {
      console.error('useBookingActiveTimers: fetch error', error.message);
      return;
    }

    const rows = (data || []).map((row: any) => ({
      id: row.id,
      resource_id: row.resource_id,
      started_at: row.started_at,
      resource: row.resources || row.resource || {},
    })) as ActiveTimerWithResource[];

    setActiveTimers(rows);
  }, [bookingId]);

  useEffect(() => {
    fetchActive();

    if (!bookingId) return;

    const channel = supabase
      .channel(`booking-active-timers-${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_time_entries',
          filter: `booking_id=eq.${bookingId}`,
        },
        () => {
          fetchActive();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [bookingId, fetchActive]);

  // Tick every second so UI can show live elapsed times
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (activeTimers.length === 0) return;
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [activeTimers.length]);

  /** Elapsed seconds for a given started_at (use with tick to update) */
  const getElapsedSeconds = useCallback((startedAt: string) => {
    return Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  }, []);

  return {
    activeTimers,
    /** Increments every second while there are active timers (use to drive live elapsed display) */
    tick,
    getElapsedSeconds,
    refresh: fetchActive,
  };
}
